import React, { Fragment, PureComponent, RefObject, SyntheticEvent } from 'react'
import {
  Editor,
  EditorState,
  RichUtils,
  Modifier,
  ContentBlock,
  ContentState,
  DraftHandleValue,
  DraftEditorCommand
} from 'draft-js'
import { EditorStateOps, TaskState } from '@src/editor-helpers'
import { handleBackspace, handleDelete } from './handleDeletes'
import keyBindings from './keyBindings'
import handleTaskDecisionCommand from './handleTaskDecisionCommand'
import handleSelectAll from './handleSelectAll'

import Header from '@components/Header'
import FocusKeyContext from '@components/FocusKeyContext'
import DecisionBlock from '@components/DecisionBlock'
import ConspectBlock from '@components/ConspectBlock'

import Styles, { Link, LinkButton, Footer, Main, Title } from './Styles'
import clipboard from 'clipboard'

type SyntheticKeyboardEvent = React.KeyboardEvent<{}>

const getTitleDate = () => {
  const date = new Date()
  return date.toLocaleTimeString('RU-ru', { day: 'numeric', month: 'long' }).replace(/:\d{1,2}$/, '')
}
class RemarksApplicationRoot extends PureComponent<{}, { editorState: EditorState; focusKey?: string; title: string }> {
  state = {
    editorState: EditorStateOps.getNewState(),
    focusKey: undefined,
    title: `Замечания ${getTitleDate()}`
  }
  returnCount = 0
  isLastReturnChange: DraftHandleValue = 'not-handled'
  editorRef: RefObject<Editor> = React.createRef()

  componentDidMount() {
    new clipboard('#copy-link', {
      text: () => EditorStateOps.getTextFromState(this.state.title, this.getEditorState())
    })
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        const el = document.querySelector('#minutes')
        if (el) el.classList.add('show-time')
      }, 100)
    })
    if (this.editorRef.current) this.editorRef.current.focus()
    this.checkFocusConspectKey()
  }

  componentDidUpdate() {
    this.checkFocusConspectKey()
  }

  checkFocusConspectKey() {
    const { editorState, focusKey } = this.state
    const newFocusKey = EditorStateOps.getFocusKeyOfFirstConspect(editorState)
    if (newFocusKey !== focusKey) this.setState({ focusKey: newFocusKey })
  }

  saveTextToBuffer = (event: any): void => event.preventDefault()

  getEditorState = () => this.state.editorState

  onChange = (editorState: EditorState) => {
    editorState = EditorStateOps.addTypeToNewBlock(editorState)
    editorState = this.checkEnterPress(editorState)
    editorState = EditorStateOps.checkSolutionPresent(editorState)
    editorState = EditorStateOps.checkTasksWithoutSolution(editorState)

    this.setState({ editorState })
  }

  checkEnterPress = (editorState: EditorState) => {
    if (this.isLastReturnChange === 'handled') {
      this.isLastReturnChange = 'not-handled'

      const currentBlock = EditorStateOps.getFocusBlock(editorState)
      const taskState = TaskState.fromEditorState(editorState)
      const task = TaskState.getTaskByKey(taskState, currentBlock.get('key'))
      const isEmpty = TaskState.isTaskTextEmpty(task)

      if (this.returnCount === 2) {
        this.returnCount = 0
        if (!isEmpty) editorState = EditorStateOps.addTask(editorState)
      }
    } else {
      this.returnCount = 0
    }

    return editorState
  }

  handleKeyCommand = (command: DraftEditorCommand, editorState: EditorState) => {
    let newState

    newState = handleBackspace(editorState, command)
    if (newState) {
      this.onChange(newState)
      return 'handled'
    }

    newState = handleDelete(editorState, command)
    if (newState) {
      this.onChange(newState)
      return 'handled'
    }

    newState = handleTaskDecisionCommand(editorState, command)
    if (newState) {
      this.onChange(newState)
      return 'handled'
    }

    newState = handleSelectAll(editorState, command)
    if (newState) {
      this.onChange(newState)
      return 'handled'
    }

    newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      this.onChange(newState)
      return 'handled'
    }

    return 'not-handled'
  }

  handleReturn = (event: SyntheticKeyboardEvent, editorState: EditorState): DraftHandleValue => {
    this.isLastReturnChange = 'handled'
    this.returnCount += 1
    // @ts-ignore
    if (this.handleReturnSoftNewline(event as KeyboardEvent)) {
      return 'handled'
    } else if (this.handleReturnAfterSolution()) {
      return 'handled'
    } else {
      return 'not-handled'
    }
  }

  handleReturnAfterSolution() {
    let editorState = this.getEditorState()
    const selectionType = EditorStateOps.getFocusBlock(editorState).get('type')
    if (selectionType === 'solution') {
      const contentState = editorState.getCurrentContent()
      const selection = editorState.getSelection()
      const afterBlock = contentState.getBlockForKey(selection.getEndKey())
      editorState = EditorStateOps.insertBlock(editorState, EditorStateOps.createContentBlock(''), afterBlock)
      this.onChange(editorState)
      return true
    } else {
      return false
    }
  }

  handleReturnSoftNewline(event: KeyboardEvent) {
    let editorState = this.getEditorState()

    if (event.shiftKey) {
      let selection = editorState.getSelection()
      let content = editorState.getCurrentContent()
      let newContent = Modifier.removeRange(content, selection, 'forward')
      let newSelection = newContent.getSelectionAfter()
      let block = newContent.getBlockForKey(newSelection.getStartKey())

      newContent = Modifier.insertText(
        newContent,
        newSelection,
        '\n',
        block.getInlineStyleAt(newSelection.getStartOffset())
      )
      this.onChange(EditorState.push(editorState, newContent, 'insert-fragment'))
      return true
    }
    return false
  }

  changeDecision = (block: ContentBlock, newBlock: ContentBlock) => {
    const editorState = this.getEditorState()
    const contentState = editorState.getCurrentContent()
    const newContentState = contentState.merge({
      blockMap: contentState.getBlockMap().set(block.getKey(), newBlock)
    }) as ContentState
    const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data')

    this.onChange(newEditorState)
  }

  blockRendererFn = (contentBlock: ContentBlock) => {
    switch (contentBlock.get('type')) {
      case 'decision':
        return {
          component: DecisionBlock,
          editable: false,
          class: 'test',
          props: {
            changeDecision: this.changeDecision,
            getEditorState: this.getEditorState
          }
        }
      case 'unstyled':
        return {
          component: ConspectBlock,
          props: {
            getEditorState: this.getEditorState
          }
        }
      default:
        return undefined
    }
  }

  blockStyleFn = (contentBlock: ContentBlock): string => {
    const type = contentBlock.getType()
    return type === 'unstyled' ? 'paragraph' : ''
  }

  changeTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ title: event.target.value })
  }

  render() {
    const { editorState, focusKey, title } = this.state
    return (
      <Fragment>
        <Main>
          <Header />
          <Styles>
            <div className="top">
              <Title value={title} onChange={this.changeTitle} />
              <div className="menu">
                <LinkButton href="##" id="copy-link" onClick={this.saveTextToBuffer}>
                  Копировать текст
                </LinkButton>
              </div>
            </div>
            <FocusKeyContext.Provider value={{ focusKey: focusKey ? focusKey : '' }}>
              <Editor
                ref={this.editorRef}
                editorState={editorState}
                onChange={this.onChange}
                handleKeyCommand={this.handleKeyCommand}
                handleReturn={this.handleReturn}
                blockRendererFn={this.blockRendererFn}
                blockStyleFn={this.blockStyleFn}
                keyBindingFn={keyBindings}
              />
            </FocusKeyContext.Provider>
            <p className="hint">
              <span>↩︎↩︎</span> Новое замечание
            </p>
          </Styles>
        </Main>
        <Footer>
          <Link target="_blank" rel="noopener noreferrer" href="/remarks/about/">
            О проекте
          </Link>
        </Footer>
      </Fragment>
    )
  }
}

export default RemarksApplicationRoot



// WEBPACK FOOTER //
// ./src/components/RemarksApplicationRoot/RemarksApplocationRoot.tsx